<template>
  <div class="form-content">
    <HiddenInput input-id="id" />
    <VPImageInput
      input-id="logoId"
      preview-field-id="logo.url"
    />
    <div class="column-content room-input">
      <VPInput
        input-id="name"
        placeholder="Add Name"
        label="Name"
      />
      <VPInput
        input-id="url"
        placeholder="Add Website"
        label="Website"
      />
      <div class="vp-rooms -mt-0.5">
        <span class="text-xs vp-label">Rooms</span>
        <FieldList
          v-slot="{ index }"
          name="rooms"
        >
          <div class="flex room-container">
            <VPInput
              :class="{ hidden: isDestroyed(index) }"
              accessible-label="Room name"
              class="flex-grow"
              input-id="name"
              placeholder="Add Room"
            />
            <HiddenInput input-id="_destroy" />
            <VPButton
              :id="btnId"
              variant="text"
              :class="{ hidden: isDestroyed(index) }"
              class="room-btn"
              @click="removeRoom(index)"
            >
              <CloseCircle class="room-btn-icon" />
              <Accessible :description="removeRoomText(index)" />
            </VPButton>
            <HiddenInput input-id="id" />
          </div>
        </FieldList>
      </div>

      <VPInput
        input-id="replyTo"
        placeholder="Add Email"
        label="Reply to"
        class="mb-1"
      />
    </div>

    <div class="column-content venue-address-input">
      <FieldsFor name="address">
        <VPAddressInput class="mb-3 pt-3" />
      </FieldsFor>
      <div class="pb-4">
        <VPDropdown
          input-id="locale"
          label="Locale"
          placeholder="Select locale"
          :options="locales"
        />
      </div>
      <div class="py-3">
        <VPDropdown
          input-id="currency"
          label="Currency"
          placeholder="Select currency"
          :options="currencies"
        />
      </div>
      <div class="py-3">
        <VPTimezoneSelect
          input-id="timezone"
          label="Timezone"
          placeholder="Select timezone"
        />
      </div>
    </div>

    <div class="column-content">
      <VPPercentageInput
        input-id="salesTax"
        placeholder="0.000%"
        label="Tax Fee (%)"
        class="text-left"
        hide-empty-input
      />
      <div class="vp-processing-fee -mt-1">
        <span class="text-xs pb-0.5 vp-label">CC Processing Fee</span>
        <div class="flex">
          <VPPercentageInput
            input-id="processingFeePercentage"
            placeholder="Add processing fee percentage"
            accessible-label="Credit card processing percentage fee"
            class="text-left"
          />
          <VPPriceInput
            input-id="processingFee"
            accessible-label="Credit card processing fixed fee"
            placeholder="Add amount"
            class="text-left"
          />
        </div>
      </div>

      <div class="my-4">
        <h3 class="my-2 font-bold text-vp-base-700">
          All-inclusive pricing
        </h3>

        <p>Select which fees will be included in the display price of the ticket at checkout:</p>

        <div class="my-2">
          <VPCheckbox
            input-id="includeConvenienceFee"
            label="Include convenience fee"
            :true-value="true"
            :false-value="false"
          />
          <p class="-mt-2 ml-9 italic text-sm">
            Platform fee + Additional fee
          </p>
        </div>

        <div class="my-2">
          <VPCheckbox
            input-id="includeTaxFee"
            label="Include tax fee"
            :true-value="true"
            :false-value="false"
          />
          <p class="-mt-2 ml-9 italic text-sm">
            Requires "Include convenience fee"
          </p>
        </div>

        <div class="my-2">
          <VPCheckbox
            input-id="includeCcFee"
            label="Include credit card processing fee"
            :true-value="true"
            :false-value="false"
          />
          <p class="-mt-2 ml-9 italic text-sm">
            Requires "Include convenience fee" and "Include tax fee"
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import VPInput from '@/components/form/VPInput.vue';
import VPButton from '@/components/form/VPButton.vue';
import VPDropdown from '@/components/form/VPDropdown.vue';
import VPAddressInput from '@/components/form/VPAddressInput.vue';
import VPTimezoneSelect from '@/components/form/VPTimezoneSelect.vue';
import VPPercentageInput from '@/components/form/VPPercentageInput.vue';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import { FieldList, HiddenInput, FieldsFor, useFormsStoreComposable } from 'vuniform';
import { computed, inject, watch, getCurrentInstance } from 'vue';
import { CloseCircle } from 'mdue';
import VPImageInput from '@/components/form/VPImageInput.vue';
import Accessible from '@/components/Accessible.vue';
import VPCheckbox from '@/components/form/VPCheckbox.vue';

const formId = inject('formId', '');
const { formsStore, fieldGetValue, formGetValues } = useFormsStoreComposable(formId);
const locales = [
  { name: 'English (US)', value: 'en' },
  { name: 'English (CA)', value: 'en-CA' },
  { name: 'French (CA)', value: 'fr-CA' },
];
const currencies = [
  { name: 'USD', value: 'usd' },
  { name: 'CAD', value: 'cad' },
];

const addNewRoom = () => {
  const { rooms = [] } = formGetValues();
  const roomsCount = rooms.length;
  formsStore.INIT_FORM_FIELD({
    formId,
    name: `rooms[${roomsCount}].name`,
    config: {},
  });
};

// Call addNewRoom if there are no rooms
const { rooms = [] } = formGetValues();
if (rooms.length === 0) {
  addNewRoom();
}

const lastRoom = computed(() => {
  const { rooms = [{}] } = formGetValues();
  const visibleRooms = rooms.filter(({ _destroy = false }) => !_destroy);
  return visibleRooms[visibleRooms.length - 1];
});

watch(
  lastRoom,
  newValue => {
    if (!newValue) {
      addNewRoom();
      return;
    }
    if (newValue?.name?.trim().length) {
      addNewRoom();
    }
  },
  { immediate: true },
);

const removeRoom = (roomIndex: number) => {
  formsStore.STAGE_FIELD_CHANGE({
    query: { formId: formId, field: `rooms[${roomIndex}]._destroy` },
    value: true,
  });
};

const isDestroyed = (roomIndex: number) => {
  const { rooms = [] } = formGetValues();
  return rooms[roomIndex]._destroy;
};

const btnId = `remove-room-${getCurrentInstance()?.uid}`;

const removeRoomText = (roomIndex: number) => {
  const { rooms = [] } = formGetValues();
  const room = rooms[roomIndex];
  return `Remove room ${room?.name || ''}`.trim();
};

// Each of the following requires previous to be checked. This is handled with watchers
const includeConvenienceFee = computed(() => fieldGetValue('includeConvenienceFee'));
const includeTaxFee = computed(() => fieldGetValue('includeTaxFee'));
const includeCcFee = computed(() => fieldGetValue('includeCcFee'));

watch(includeCcFee, newValue => {
  if (newValue && !includeTaxFee.value) {
    formsStore.STAGE_FIELD_CHANGE({
      query: { formId, field: 'includeTaxFee' },
      value: true,
    });
  }
});

watch(includeTaxFee, newValue => {
  if (newValue && !includeConvenienceFee.value) {
    formsStore.STAGE_FIELD_CHANGE({
      query: { formId, field: 'includeConvenienceFee' },
      value: true,
    });
  } else if (!newValue && includeCcFee.value) {
    formsStore.STAGE_FIELD_CHANGE({
      query: { formId, field: 'includeCcFee' },
      value: false,
    });
  }
});

watch(includeConvenienceFee, newValue => {
  if (!newValue && includeTaxFee.value) {
    formsStore.STAGE_FIELD_CHANGE({
      query: { formId, field: 'includeTaxFee' },
      value: false,
    });
  }
});
</script>
